import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { CheckCircle2, Clock, XCircle, Lock, Mic } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [queuePos, setQueuePos] = useState(null);
  const attempts = useRef(0);
  const { t } = useLang();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 12) {
        if (attempts.current >= 12) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setQueuePos(data.queue_position || null); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { console.debug("Payment status not ready yet, retrying", e); }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-6 py-24 text-center">
      {state === "checking" && (
        <>
          <Clock size={48} className="text-emerald-400 mx-auto mb-6 animate-pulse" />
          <h1 className="text-3xl font-black tracking-tighter text-white mb-3">{t("pay_checking")}</h1>
          <p className="text-zinc-400">{t("pay_checking_sub")}</p>
        </>
      )}
      {state === "paid" && (
        <div data-testid="payment-confirmed">
          <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-6" />
          <h1 className="text-3xl font-black tracking-tighter text-white mb-3">{t("pay_ok")}</h1>
          <p className="text-zinc-400 mb-6">{t("pay_ok_sub")}</p>
          {queuePos && (
            <div data-testid="success-queue-position" className="bg-emerald-950/30 border border-emerald-800/50 rounded-2xl p-5 mb-4 flex items-center justify-center gap-3">
              <Mic size={20} className="text-emerald-400" />
              <p className="text-base font-semibold text-emerald-300">{t("pay_queue_pos", { n: queuePos })}</p>
            </div>
          )}
          <div className="bg-amber-950/20 border border-amber-800/40 rounded-2xl p-6 flex items-start gap-4 text-left mb-8">
            <Lock size={20} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-200/80 leading-relaxed">{t("pay_escrow_note")}</p>
          </div>
          <Link to="/dashboard" data-testid="success-go-dashboard-link" className="text-emerald-400 font-semibold hover:text-emerald-300">
            {t("pay_view")}
          </Link>
        </div>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle size={48} className="text-red-400 mx-auto mb-6" />
          <h1 className="text-3xl font-black tracking-tighter text-white mb-3">
            {state === "timeout" ? t("pay_timeout") : t("pay_error")}
          </h1>
          <p className="text-zinc-400 mb-8">
            {state === "timeout" ? t("pay_timeout_sub") : t("pay_error_sub")}
          </p>
          <Link to="/descubrir" className="text-emerald-400 font-semibold hover:text-emerald-300">{t("pay_back")}</Link>
        </>
      )}
    </div>
  );
}
